import {useCallback, useState} from 'react';
import {DragStart, DragUpdate} from '@hello-pangea/dnd';

const queryAttr = 'data-rbd-draggable-id';

export interface DndDropPlaceholderProps {
  clientHeight: number;
  clientWidth: number;
  clientY: number;
  clientX: number;
  droppableTargetId?: string;
}

const getDraggedDom = (draggableId: string) => {
  const domQuery = `[${queryAttr}='${draggableId}']`;
  const draggedDOM = document.querySelector(domQuery);
  return draggedDOM;
};

export function useDndDropPlaceholder() {
  const [placeholderProps, setPlaceholderProps] = useState<DndDropPlaceholderProps | null>(null);

  const calculateClientY = useCallback((paddingTop: number, childrenElementsArray: Element[], index: number) => {
    return (
      childrenElementsArray.slice(0, index).reduce((total, curr) => {
        if (curr) {
          const style = window.getComputedStyle(curr);
          const marginBottom = parseFloat(style.marginBottom);
          const height = curr.getBoundingClientRect().height;
          return total + height + marginBottom;
        }
        return 0;
      }, 0) + paddingTop
    );
  }, []);

  const onDragStart = useCallback(
    (event: DragStart) => {
      const draggedDOM = getDraggedDom(event.draggableId);

      if (!(draggedDOM && draggedDOM.parentElement)) {
        return;
      }

      const { clientHeight, clientWidth } = draggedDOM;
      const paddingTop = parseFloat(window.getComputedStyle(draggedDOM.parentElement).paddingTop);

      const clientY = calculateClientY(paddingTop, Array.from(draggedDOM.parentElement.children), event.source.index);
      setPlaceholderProps({
        clientHeight,
        clientWidth,
        clientY,
        clientX: parseFloat(window.getComputedStyle(draggedDOM.parentElement).paddingLeft),
        droppableTargetId: event.source.droppableId,
      });
    },
    [calculateClientY]
  );

  const onDragUpdate = useCallback(
    (event: DragUpdate) => {
      const draggedDOM = getDraggedDom(event.draggableId);
      if (!(draggedDOM && draggedDOM.parentElement && event.destination)) {
        return;
      }

      const { clientHeight, clientWidth } = draggedDOM;
      const sourceIndex = event.source.index;
      const destinationIndex = event.destination.index;
      const childrenArray = Array.from(draggedDOM.parentElement.children);
      const paddingTop = parseFloat(window.getComputedStyle(draggedDOM.parentElement).paddingTop);
      const movedItem = childrenArray[sourceIndex];
      childrenArray.splice(sourceIndex, 1);

      const updatedArray = [
        ...childrenArray.slice(0, destinationIndex),
        movedItem,
        ...childrenArray.slice(destinationIndex + 1),
      ];
      const clientY = calculateClientY(paddingTop, updatedArray, destinationIndex);
      setPlaceholderProps({
        clientHeight,
        clientWidth,
        clientY,
        clientX: parseFloat(window.getComputedStyle(draggedDOM.parentElement).paddingLeft),
        droppableTargetId: event.destination.droppableId,
      });
    },
    [calculateClientY]
  );

  const onDragEnd = useCallback(() => {
    setPlaceholderProps(null);
  }, []);

  return {
    placeholderProps,
    onDragStart,
    onDragUpdate,
    onDragEnd,
  };
}
